import { Component } from '@theme/component';

/** Marks the announcement currently on screen. The CSS fades everything else out. */
const ACTIVE_ATTRIBUTE = 'data-r-active';

/** Set on the track once this component owns which slide is showing. */
const READY_ATTRIBUTE = 'rSlidesReady';

/* ↗ .cursor/references/code-notes/r-announcement-slides.js.md#§1 */
const MIN_INTERVAL_SECONDS = 2;
const DEFAULT_INTERVAL_SECONDS = 5;

/* ↗ .cursor/references/code-notes/r-announcement-slides.js.md#§7 */
const SETTLE_AFTER_INPUT_MS = 2000;

/* ↗ .cursor/references/code-notes/r-announcement-slides.js.md#§8 */
const SWIPE_MIN_DISTANCE_PX = 40;
const SWIPE_HORIZONTAL_RATIO = 2;
const SWIPE_MIN_DURATION_MS = 100;
const SWIPE_MAX_DURATION_MS = 1000;

/** Fallback for the fade lock if the slide's computed transition cannot be read. */
const FALLBACK_FADE_MS = 400;

/** Placeholder in the dot's localised label, standing in for the 1-based position. */
const DOT_LABEL_PLACEHOLDER = '%d';

/** Published on the host so the CSS can reserve the dot row's width. ↗ §11 */
const DOT_COUNT_PROPERTY = '--r-announcement-dot-count';

/**
 * How long the incoming announcement takes to arrive: the delay it waits out while
 * the outgoing one fades, plus its own fade-in. Read off the slide's resolved
 * `transition-*` values rather than off the tokens that feed them, so the lock
 * tracks whatever the CSS is actually doing — including the 0 a reduced-motion
 * visitor gets. ↗ .cursor/references/code-notes/r-announcement-slides.js.md#§10
 *
 * @param {HTMLElement} slide
 * @returns {number}
 */
function fadeMs(slide) {
  // Both lists are ordered by transition-property; opacity is declared first.
  const styles = getComputedStyle(slide);
  const seconds = (/** @type {string} */ list) => Number.parseFloat(list.split(',')[0] ?? '');
  const total = (seconds(styles.transitionDelay) + seconds(styles.transitionDuration)) * 1000;

  return Number.isFinite(total) && total >= 0 ? total : FALLBACK_FADE_MS;
}

/* ↗ .cursor/references/code-notes/r-announcement-slides.js.md#§2 */
class AnnouncementSlides extends Component {
  requiredRefs = ['track'];

  /** @type {HTMLElement[]} */
  #slides = [];

  /** Index into `#slides` of the announcement on screen. @type {number} */
  #index = 0;

  /** Has the initial `auto_rotate` state been applied? @type {boolean} */
  #settled = false;

  /** Fade in flight — manual input is absorbed until it clears. @type {boolean} */
  #transitioning = false;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #transitionTimer = null;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #settleTimer = null;

  /** Open swipe gesture, if a pointer is down on the bar. @type {{id: number, x: number, y: number, at: number} | null} */
  #gesture = null;

  /** @type {ReturnType<typeof setInterval> | null} */
  #timer = null;

  /* ↗ .cursor/references/code-notes/r-announcement-slides.js.md#§3 */
  /** @type {Set<string>} */
  #holds = new Set();

  connectedCallback() {
    super.connectedCallback();

    this.#collect();

    this.addEventListener('pointerenter', this.#holdPointer);
    this.addEventListener('pointerleave', this.#releasePointer);
    this.addEventListener('focusin', this.#holdFocus);
    this.addEventListener('focusout', this.#releaseFocus);
    document.addEventListener('visibilitychange', this.#syncVisibility);

    this.refs.toggle?.addEventListener('click', this.#onToggle);
    this.refs.dots?.addEventListener('click', this.#onDotClick);

    // ↗ .cursor/references/code-notes/r-announcement-slides.js.md#§8
    this.addEventListener('pointerdown', this.#onPointerDown);
    this.addEventListener('pointerup', this.#onPointerUp);
    this.addEventListener('pointercancel', this.#onPointerCancel);

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:block:select', this.#onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.#onBlockDeselect);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#stop();
    this.removeEventListener('pointerenter', this.#holdPointer);
    this.removeEventListener('pointerleave', this.#releasePointer);
    this.removeEventListener('focusin', this.#holdFocus);
    this.removeEventListener('focusout', this.#releaseFocus);
    document.removeEventListener('visibilitychange', this.#syncVisibility);
    document.removeEventListener('shopify:block:select', this.#onBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.#onBlockDeselect);
    this.refs.toggle?.removeEventListener('click', this.#onToggle);
    this.refs.dots?.removeEventListener('click', this.#onDotClick);
    this.removeEventListener('pointerdown', this.#onPointerDown);
    this.removeEventListener('pointerup', this.#onPointerUp);
    this.removeEventListener('pointercancel', this.#onPointerCancel);

    if (this.#transitionTimer != null) clearTimeout(this.#transitionTimer);
    if (this.#settleTimer != null) clearTimeout(this.#settleTimer);
    this.#transitionTimer = this.#settleTimer = null;
  }

  /**
   * morph swaps the track's children in place when the Section Rendering API
   * re-renders the bar — the element itself never disconnects, so the slide list
   * this holds would otherwise point at detached nodes.
   */
  updatedCallback() {
    super.updatedCallback();
    this.#collect();
  }

  /** Dwell per announcement, in milliseconds. */
  get #interval() {
    const seconds = Number.parseFloat(this.dataset.interval ?? '');
    const resolved = Number.isFinite(seconds) ? seconds : DEFAULT_INTERVAL_SECONDS;
    return Math.max(resolved, MIN_INTERVAL_SECONDS) * 1000;
  }

  get #autoplay() {
    return this.dataset.autoplay !== 'false';
  }

  /* ↗ .cursor/references/code-notes/r-announcement-slides.js.md#§4 */
  #collect() {
    const previous = this.#slides[this.#index];

    this.#slides = /** @type {HTMLElement[]} */ ([
      ...this.refs.track.querySelectorAll(':scope > .announcement-bar__item'),
    ]);

    if (this.#slides.length === 0) {
      this.#stop();
      delete this.refs.track.dataset[READY_ATTRIBUTE];
      this.#renderDots();
      this.#setControlsVisible(false);
      return;
    }

    // Stay on the announcement that was showing if it survived the re-render.
    const kept = previous ? this.#slides.indexOf(previous) : -1;
    this.#show(kept === -1 ? 0 : kept);
    this.#renderDots();
    this.refs.track.dataset[READY_ATTRIBUTE] = '';

    // The controls are offered whenever there is a rotation to control — including
    // when the merchant has auto-rotate switched off, which is the only way the
    // announcements after the first are reachable at all in that mode.
    this.#setControlsVisible(this.#slides.length > 1);
    if (!this.#settled) {
      this.#settled = true;
      if (!this.#autoplay) this.#setPaused(true);
    }
    this.#syncVisibility();
  }

  /**
   * Put one announcement on screen and take every other one out of the tab order.
   * @param {number} index
   */
  #show(index) {
    if (this.#slides.length === 0) return;

    this.#index = ((index % this.#slides.length) + this.#slides.length) % this.#slides.length;

    for (const [position, slide] of this.#slides.entries()) {
      slide.toggleAttribute(ACTIVE_ATTRIBUTE, position === this.#index);
    }

    this.#syncDots();
  }

  #next = () => this.#show(this.#index + 1);

  /* ↗ .cursor/references/code-notes/r-announcement-slides.js.md#§9 */
  /** @param {number} index */
  #goTo(index) {
    if (this.#transitioning || this.#slides.length < 2) return;

    this.#show(index);
    this.#lockForFade();
    this.#settle();
  }

  /** Relative move — the swipe gesture's form of the same thing. @param {number} delta */
  #navigate(delta) {
    this.#goTo(this.#index + delta);
  }

  /**
   * One listener on the dot row rather than one per dot: the row is rebuilt
   * whenever the slide list changes, and a delegated listener survives that.
   * @param {MouseEvent} event
   */
  #onDotClick = (event) => {
    const dot = /** @type {HTMLElement | null} */ (event.target)?.closest?.('.announcement-bar__dot');
    if (!(dot instanceof HTMLElement)) return;

    const index = Number.parseInt(dot.dataset.index ?? '', 10);
    if (Number.isInteger(index) && index !== this.#index) this.#goTo(index);
  };

  /* ↗ .cursor/references/code-notes/r-announcement-slides.js.md#§11 */
  #renderDots() {
    const { dots } = this.refs;
    if (!(dots instanceof HTMLElement)) return;

    const count = this.#slides.length > 1 ? this.#slides.length : 0;

    // The CSS reserves the row's width from this, so it is published even when the
    // answer is "no dots" — the reservation has to collapse with them.
    this.style.setProperty(DOT_COUNT_PROPERTY, String(count));

    if (dots.childElementCount !== count) {
      const label = dots.dataset.labelDot ?? '';
      dots.replaceChildren(
        ...Array.from({ length: count }, (_, position) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'announcement-bar__dot';
          dot.dataset.index = String(position);
          dot.setAttribute('aria-label', label.replace(DOT_LABEL_PLACEHOLDER, String(position + 1)));
          return dot;
        })
      );
    }

    this.#syncDots();
  }

  /** Mark the dot for the announcement on screen. ↗ §11 */
  #syncDots() {
    const { dots } = this.refs;
    if (!(dots instanceof HTMLElement)) return;

    for (const [position, dot] of [...dots.children].entries()) {
      if (position === this.#index) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    }
  }

  /* ↗ .cursor/references/code-notes/r-announcement-slides.js.md#§10 */
  #lockForFade() {
    if (this.#transitionTimer != null) clearTimeout(this.#transitionTimer);

    const incoming = this.#slides[this.#index];
    const ms = incoming ? fadeMs(incoming) : FALLBACK_FADE_MS;

    this.#transitioning = true;
    this.#transitionTimer = setTimeout(() => {
      this.#transitioning = false;
      this.#transitionTimer = null;
    }, ms);
  }

  /* ↗ .cursor/references/code-notes/r-announcement-slides.js.md#§7 */
  #settle() {
    this.#hold('input', true);
    if (this.#settleTimer != null) clearTimeout(this.#settleTimer);
    this.#settleTimer = setTimeout(() => {
      this.#settleTimer = null;
      this.#hold('input', false);
    }, SETTLE_AFTER_INPUT_MS);
  }

  /* ↗ .cursor/references/code-notes/r-announcement-slides.js.md#§8 */
  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    if (event.pointerType === 'mouse') return;
    if (/** @type {HTMLElement} */ (event.target)?.closest?.('a, button')) return;

    this.#gesture = { id: event.pointerId, x: event.clientX, y: event.clientY, at: Date.now() };
    this.#hold('swipe', true);

    // Without capture, a thumb that leaves the 30px strip mid-drag never delivers
    // `pointerup` here and the gesture would hang holding the rotation stopped.
    try {
      this.setPointerCapture(event.pointerId);
    } catch {
      /* capture is best-effort; the pointercancel path covers the rest */
    }
  };

  /** @param {PointerEvent} event */
  #onPointerUp = (event) => {
    const gesture = this.#gesture;
    if (!gesture || gesture.id !== event.pointerId) return;
    this.#gesture = null;

    const dx = gesture.x - event.clientX;
    const dy = gesture.y - event.clientY;
    const elapsed = Date.now() - gesture.at;

    const deliberate = elapsed > SWIPE_MIN_DURATION_MS && elapsed < SWIPE_MAX_DURATION_MS;
    const horizontal = Math.abs(dx) > Math.abs(dy) * SWIPE_HORIZONTAL_RATIO;
    const far = Math.abs(dx) > SWIPE_MIN_DISTANCE_PX;

    // Navigate before releasing the swipe hold: #navigate takes its own 'input'
    // hold, so the rotation goes straight from stopped-by-swipe to settling rather
    // than restarting for one frame in between.
    if (deliberate && horizontal && far) this.#navigate(dx > 0 ? 1 : -1);
    this.#hold('swipe', false);
  };

  #onPointerCancel = () => {
    this.#gesture = null;
    this.#hold('swipe', false);
  };

  #start() {
    if (this.#timer != null) return;
    if (this.#slides.length < 2 || this.#holds.size > 0) return;

    this.#timer = setInterval(this.#next, this.#interval);
  }

  #stop() {
    if (this.#timer == null) return;
    clearInterval(this.#timer);
    this.#timer = null;
  }

  /**
   * @param {string} reason
   * @param {boolean} held
   */
  #hold(reason, held) {
    if (held) this.#holds.add(reason);
    else this.#holds.delete(reason);

    if (this.#holds.size > 0) this.#stop();
    else this.#start();
  }

  #holdPointer = () => this.#hold('pointer', true);
  #releasePointer = () => this.#hold('pointer', false);
  #holdFocus = () => this.#hold('focus', true);

  /**
   * `focusout` fires for movement *within* the bar too — between the pause control
   * and a slide's link — so the hold only lifts once focus has actually left.
   * @param {FocusEvent} event
   */
  #releaseFocus = (event) => {
    const next = /** @type {Node | null} */ (event.relatedTarget);
    if (next && this.contains(next)) return;
    this.#hold('focus', false);
  };

  /* ↗ .cursor/references/code-notes/r-announcement-slides.js.md#§5 */
  #syncVisibility = () => this.#hold('hidden', document.visibilityState === 'hidden');

  #onToggle = () => this.#setPaused(!this.#holds.has('user'));

  /**
   * The merchant-or-visitor pause, and the control that presents it. Separate from
   * `#hold` because this is the one reason with a UI attached — and because the
   * `auto_rotate` setting starts the bar in exactly this state.
   *
   * @param {boolean} paused
   */
  #setPaused(paused) {
    this.#hold('user', paused);

    const { toggle, toggleLabel } = this.refs;
    if (!(toggle instanceof HTMLElement)) return;

    toggle.setAttribute('aria-pressed', String(paused));
    toggle.dataset.paused = paused ? 'true' : 'false';

    const label = paused ? toggle.dataset.labelPlay : toggle.dataset.labelPause;
    if (toggleLabel instanceof HTMLElement && label) toggleLabel.textContent = label;
  }

  /** @param {boolean} visible */
  #setControlsVisible(visible) {
    const { controls } = this.refs;
    if (controls instanceof HTMLElement) controls.hidden = !visible;
    if (!visible) this.#hold('user', false);
  }

  /* ↗ .cursor/references/code-notes/r-announcement-slides.js.md#§6 */
  /** @param {Event} event */
  #onBlockSelect = (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);
    const slide = target?.closest?.('.announcement-bar__item');
    if (!slide || !this.contains(slide)) return;

    const index = this.#slides.indexOf(/** @type {HTMLElement} */ (slide));
    if (index === -1) return;

    this.#hold('editor', true);
    this.#show(index);
  };

  /** @param {Event} event */
  #onBlockDeselect = (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);
    if (target && !this.contains(target)) return;
    this.#hold('editor', false);
  };
}

if (!customElements.get('r-announcement-slides')) {
  customElements.define('r-announcement-slides', AnnouncementSlides);
}
